import React, { Component, ErrorInfo, ReactNode } from 'react'
import * as Sentry from '@sentry/react'
import Card, { CardBody } from './Card'
import Button from './Button'
import { Link } from 'react-router-dom'
import { logger } from '../utils/logger'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error('ErrorBoundary caught an error:', error, errorInfo)
    Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } })

    // Auto-reload on ChunkLoadError (stale deployment chunks)
    const isChunkError =
      error.name === 'ChunkLoadError' ||
      error.message?.includes('Loading chunk') ||
      error.message?.includes('Failed to fetch dynamically imported module')
    if (isChunkError) {
      const reloadKey = 'chunk_error_reload'
      const lastReload = sessionStorage.getItem(reloadKey)
      // Only auto-reload once per session to avoid infinite loop
      if (!lastReload) {
        sessionStorage.setItem(reloadKey, Date.now().toString())
        window.location.reload()
        return
      }
    }
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback error={this.state.error} />
    }

    return this.props.children
  }
}

function ErrorFallback({ error }: { error: Error | null }) {

  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      padding: '2rem',
      backgroundColor: 'var(--color-background, #f5f5f5)'
    }}>
      <Card style={{ maxWidth: '600px' }}>
        <CardBody>
          <h1 style={{ marginTop: 0, color: 'var(--color-danger, #C4554D)' }}>
            Something went wrong
          </h1>
          <p>
            We encountered an unexpected error. Please try refreshing the page or contact support if the problem persists.
          </p>
          {error && !import.meta.env.PROD && (
            <details style={{ marginTop: '1rem', padding: '1rem', backgroundColor: 'var(--color-surface, #fff)', borderRadius: 0 }}>
              <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>Error Details</summary>
              <pre style={{ marginTop: '0.5rem', overflow: 'auto', fontSize: '0.875rem' }}>
                {error.toString()}
                {error.stack && `\n\n${error.stack}`}
              </pre>
            </details>
          )}
          <div style={{ marginTop: '1.5rem', display: 'flex', gap: '1rem' }}>
            <Button
              variant="primary"
              onClick={() => window.location.reload()}
            >
              Refresh Page
            </Button>
            <Link to="/dashboard">
              <Button variant="outline">
                Go to Dashboard
              </Button>
            </Link>
          </div>
        </CardBody>
      </Card>
    </div>
  )
}
